<template>
  <div>
    <b-form @submit.prevent="searchReach">
      <b-form-row class="mb-2">
        <b-col
          sm="6"
          md="3"
          lg="2"
        >
          <label for="reach-date-from">Från datum</label>
          <date-picker
            id="reach-date-from"
            v-model="dateFrom"
            :months="form.datePicker.months"
            :weekdays="form.datePicker.weekdays"         
            :show-week-number="true"
            class="datepicker"
            value-type="format"
            :disabled-date="isFromDateDisabled"
            :clearable="false"
          />
        </b-col>

        <b-col
          sm="6"
          md="3"
          lg="2"
        >
          <label for="reach-date-to">Till datum</label>
          <date-picker
            id="reach-date-to"
            v-model="dateTo"
            :months="form.datePicker.months"
            :weekdays="form.datePicker.weekdays"            
            :show-week-number="true"
            class="datepicker"
            value-type="format"
            :disabled-date="isEndDateDisabled"
            :clearable="false"
          />
        </b-col>

        <b-col
          sm="6"
          md="3"
          lg="2"
          style="min-width: 13.5rem;"
        >
          <label for="target-group">Reachvillkor</label>
          <b-form-select
            id="target-group"
            v-model="minViewingTimeSelection"
            :options="minViewingTimeOptions"
            size="sm"
          />
        </b-col>

        <b-col
          sm="6"
          md="3"
          lg="2"
          class="d-flex"
        >
          <b-button
            id="refresh-reach"
            type="submit"
            class="float-right searchbutton mt-auto"
            variant="primary"
            size="sm"
          >
            <b-icon icon="arrow-repeat" /> Uppdatera
          </b-button>
        </b-col>
      </b-form-row>
      <b-form-row
        v-if="autoChangeEndDate"
        class="mb-2 redText"
      >
        <b-col
          sm="12"
          md="6"
          lg="4"
        >
          <span>Dina val översteg begränsningen i att konsumtionsperioden får vara max 28 dagar vid
            räckvidd & frekvens. Vi har därför nu automatiskt justerat slutdatumet till ett kortare
            intervall.</span>
        </b-col>
      </b-form-row>
    </b-form>

    <div
      v-if="loading && hasMeasures"
      class="text-center mb-2"
    >
      <hr>
      <b-spinner
        variant="primary"
        type="grow"
        label="Spinning"
      />
      <div>
        <small class="text-muted">Laddar räckvidd och frekvens...</small>
      </div>
    </div>
    <div v-else-if="hasMeasures">
      <hr>
      <p class="mb-0">
        <small>
          Antal objekt:
          <span class="font-weight-bold"> {{ numberOfSelectedRows }} </span>
        </small>
      </p>
      <p>
        <small>
          Paneldag: <span class="font-weight-bold"> {{ panelDay }} </span>
        </small>
      </p>
      <div class="d-flex">
        <b-form-radio-group
          v-model="measureTypeSelected"
          class="mr-auto"
          :options="measureTypeOptions"
          name="Relativ eller absolut"
          buttons
          button-variant="outline-primary"
        />
        <ProgramReachExport
          :header-items="headerItems"
          :json="items"
        />
      </div>

      <b-table
        v-if="meassureType === 'absolute_reach'"
        id="reach-summary"
        class="mt-2"
        :bordered="true"
        :striped="true"
        :head-variant="'dark'"
        :items="items"
        :fields="fieldsAbs"
        :small="true"
        :responsive="true"
      />
      <b-table
        v-else-if="meassureType === 'relative_reach'"
        id="reach-summary"
        class="mt-2"
        :bordered="true"
        :striped="true"
        :head-variant="'dark'"
        :items="items"
        :fields="fieldsRel"
        :small="true"
        :responsive="true"
      />
    </div>
    <div
      v-else
      class="text-center mb-2"
    >
      <hr>
      <small
        class="text-danger"
      ><strong>Du måste välja målgrupp och måttet Reach under Målgrupper / Mått</strong></small>
    </div>
  </div>
</template>

<script>
import ProgramReachExport from '../../Layout/Programs/ProgramReachExport.vue';
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import 'vue2-datepicker/locale/sv';
import {
  isDateNotBetweenTwoDates,
  addDays,
  daysBetween,
} from '../../../assets/js/utils.js';
import { mapGetters } from 'vuex';
import dayjs from "dayjs";

export default {
  name: 'Reach',
  components: {
    DatePicker,
    ProgramReachExport,
  },
  data() {
    return {
      meassureType: 'absolute_reach',
      form: {
        datePicker: {
          weekdays: ['Må', 'Ti', 'On', 'To', 'Fr', 'Lö', 'Sö'],
          months: [
            'Januari',
            'Februari',
            'Mars',
            'April',
            'Maj',
            'Juni',
            'Juli',
            'Augusti',
            'September',
            'Oktober',
            'November',
            'December',
          ],
        },
      },
    };
  },
  computed: {
    ...mapGetters([
      'items',
      'loading',
      'fieldsAbs',
      'fieldsRel',
      'uniSize',
      'resultCount',
      'panelDay',
    ]),
    ...mapGetters('searchApiBody', ['getSelectedPeriod']),
    minViewingTimeSelection: {
      get() {
        return this.$store.state.reachForm.min_viewing_time.selected;
      },
      set(value) {
        this.$store.commit('reachForm/updateMinViewingTime', value);
        this.$store.commit('reachApiBody/updateMinViewingTime', value);
      },
    },
    minViewingTimeOptions() {
      return this.$store.state.reachForm.min_viewing_time.options;
    },
    measureTypeSelected: {
      get() {
        return this.$store.state.reachForm.measure_type.selected;
      },
      set(value) {
        this.meassureType = value;
        this.$store.commit('reachForm/updateMeasureType', value);
        this.$store.commit('reachApiBody/updateMeasureType', value);
      },
    },
    measureTypeOptions() {
      return this.$store.state.reachForm.measure_type.options;
    },
    dateFrom: {
      get() {
        return this.$store.state.reachApiBody.payload.start_date;
      },
      set(value) {
        this.$store.commit('reachApiBody/updateStartDate', value);
      },
    },
    dateTo: {
      get() {
        return this.$store.state.reachApiBody.payload.end_date;
      },
      set(value) {
        this.$store.commit('reachApiBody/updateEndDate', value);
      },
    },
    measures() {
      return this.$store.state.reachApiBody.payload.measures;
    },
    titleIds() {
      if (this.$store.state.reachApiBody.payload.title_codes != null) {
        return this.$store.state.reachApiBody.payload.title_codes;
      } else {
        return [];
      }
    },
    hasMeasures() {
      let measures = this.$store.state.reachApiBody.payload.measures;

      if (this.getSelectedPeriod == 'publicationperiod') {
        measures = measures.filter(function(obj) {
          return obj.consolidation_days != -1;
        });
      } else {
        measures = measures.filter(function(obj) {
          return obj.consolidation_days == -1;
        });
      }
      return measures.length > 0;
    },
    numberOfSelectedRows() {
      return this.$store.state.reachApiBody.payload.title_codes.length;
    },
    autoChangeEndDate() {
      return this.$store.state.reachApiBody.autoChangeEndDate;
    },
    headerItems() {
      return [
        {
          date_key: 'Period',
          measure_1: `${this.dateFrom}--${this.dateTo}`,
        },
        {
          date_key: 'Paneldag',
          measure_1: this.panelDay,
        },
        {
          date_key: 'Antal objekt',
          measure_1: this.numberOfSelectedRows,
        },
      ];
    },
  },

  watch: {
    '$store.state.reachResults.showToastError'() {
      if (this.$store.state.reachResults.showToastError) {
        this.$bvToast.toast('Okänt serverfel vid hämtning av sökresultat r&f program', {
          title: 'Fel',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          bodyOutputType: 'trustedHtml',
        });
        this.$store.state.reachResults.showToastError = false;
      }
    },
    "$store.state.reachApiBody.payload.start_date"(startDate) {
        let validStartDate =
            dayjs(startDate, "YYYY-MM-DD").format("YYYY-MM-DD") === startDate;
          let validEndDate =
            dayjs(this.dateTo, "YYYY-MM-DD").format("YYYY-MM-DD") === this.dateTo;
          if (!validEndDate || !validStartDate) {
            return;
          }
          if (startDate > this.dateTo)
            this.$store.commit("reachApiBody/updateEndDate", startDate);
      },
      "$store.state.reachApiBody.payload.end_date"(endDate) {
          let validStartDate =
              dayjs(this.dateFrom, "YYYY-MM-DD").format("YYYY-MM-DD") === this.dateFrom;
            let validEndDate =
              dayjs(endDate, "YYYY-MM-DD").format("YYYY-MM-DD") === endDate;
            if (!validEndDate || !validStartDate) {
              return;
            }
            if (this.dateFrom > endDate)
              this.$store.commit("reachApiBody/updateStartDate", endDate);
      }
  },
  methods: {
    isFromDateDisabled(date) {
      let disableDate = isDateNotBetweenTwoDates(
        date,
        new Date(this.$store.state.systemInfo.publishedFromDate),
        new Date(this.$store.state.systemInfo.publishedToDate),
      );
      return disableDate;
    },
    isEndDateDisabled(date) {
      let disableDate = isDateNotBetweenTwoDates(
        date,
        new Date(this.$store.state.systemInfo.publishedFromDate),
        new Date(this.$store.state.systemInfo.publishedToDate),
      );
      return disableDate;
    },
    async searchReach() {
      if (
        this.$store.state.reachApiBody.payload.start_date >
        this.$store.state.reachApiBody.payload.end_date
      ) {
        this.$bvToast.toast('Från-datum får inte vara större än till-datum.', {
          title: 'Ej giltig sökperiod',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          bodyOutputType: 'trustedHtml',
        });
        return;
      }
      if (
        this.$store.state.reachApiBody.payload.start_date <
        this.$store.state.systemInfo.publishedFromDate
      ) {
        this.$bvToast.toast(
          'Perioden får inte starta före ' + this.$store.state.systemInfo.publishedFromDate + '.',
          {
            title: 'Ej giltig sökperiod',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
            bodyOutputType: 'trustedHtml',
          },
        );
        return;
      }
      if (
        this.$store.state.reachApiBody.payload.end_date >
        this.$store.state.systemInfo.publishedToDate
      ) {
        this.$bvToast.toast(
          'Till-datum får inte vara större än senast publicerings datum som är  ' +
            this.$store.state.systemInfo.publishedToDate +
            '.',
          {
            title: 'Ej giltig sökperiod',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000,
            bodyOutputType: 'trustedHtml',
          },
        );
        return;
      }
      if (this.minViewingTimeSelection == -1) {
        this.$bvToast.toast('Du måste välja ett reachvillkor.', {
          title: 'Ej giltigt reachvillkor',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000,
          bodyOutputType: 'trustedHtml',
        });
        return;
      }
      let startDate = this.$store.state.reachApiBody.payload.start_date;
      let endDate = this.$store.state.reachApiBody.payload.end_date;
      let days = daysBetween(startDate, endDate);
      //Max days in reach is 28
      if (days >= this.config_MaxReachDays) {
        endDate = addDays(startDate, this.config_MaxReachDays - 1);
        this.$store.commit('reachApiBody/updateAutoChangeEndDate', true);
        this.$store.commit('reachApiBody/updateEndDate', endDate);
      } else {
        this.$store.commit('reachApiBody/updateAutoChangeEndDate', false);
      }

      this.$emit('onSearchReach');
    },
    round(value, precision) {
      let multiplier = Math.pow(10, precision || 0);
      return (Math.round(value * multiplier) / multiplier).toString().replace('.', ',');
    },
  },
};
</script>
<style scoped>
.redText {
  color: red;
  font-weight: bold;
  font-size: 0.8em;
}
</style>